/* ============================================================================
   nt-measure.js — SHARED MEASUREMENT / QUOTING MODULE (task #50)

   The ONE place artwork dimensions are measured for quoting. Every engine
   (bySize_popular, pdp_transferBySize, bysize-v4, __blanksUploadHelper) is
   converging on this module; do not add a private dimension path to an
   engine again — that drift is how four different spellings of one constant
   (4.16 / 4.167 / 4.1666… / 4.168…) and the v4 vector 4.167× oversizing
   happened.

   ⚠ CONTRACT — QUOTING ONLY. This module returns NUMBERS, never URLs.
   NEVER use it (or its internal render URLs) to build an order, cart, or
   production URL. Quoting cannot damage a print file; asset selection can —
   they are separate modules by design (Owen 2026-07-28). The one-URL-two-
   purposes mistake caused the cart-zoom bug (PR #223) and the PixelCut
   w=2000 production leak (PR #230).

   THE UNIT RULE (verified 2026-07-28 against purchased variant_title
   history — the size customers actually bought):

       trueInches = trimmedRenderPx / (vector 72 | raster 300)

   imgix rasterises VECTOR artwork in PostScript POINTS (1pt = 1/72in), so a
   vector render's pixel size IS its true size at 72dpi. Raster sources are
   already pixels, read against the 300dpi print basis. pdf/psd go to
   calculateInches in s3Key mode, which returns TRUE inches for those types
   (verified with a hand-built exactly-10×10in PDF → 10.00 × 10.00).

   ⚠ Do NOT reintroduce calculateInches for eps/ai and do NOT re-add a
   ×4.16* constant: calculateInches returns DIFFERENT UNITS per call mode
   (?url= is a 72dpi render ÷300; ?s3Key= is inconsistent per stored object)
   and its dpi field cannot disambiguate.

   ⚠ Measurement renders must NEVER carry w=/h= (a kept w=2000 is why every
   wide file quoted exactly 6.67") and never bg-remove without the bbox
   method below (imgix DOWNSCALES any bg-remove render that has no explicit
   w — 6144px arrived as 3814px — so its pixel count is not source pixels).

   BG-REMOVED SIZING (content-fraction / mask-bbox method): the customer is
   charged for the art that remains after background removal, but no single
   render both preserves the source pixel basis and shows the removal result.
   So: measure the UNTRIMMED bg-removed render at a known width (≤ source, so
   nothing upscales; no trim, so frame→source mapping stays linear), scan its
   alpha bounding box on canvas, and rescale:

       contentSourcePx = bboxRenderPx × (sourceFramePx / renderFramePx)

   ~1.5% accuracy from bbox quantisation at reduced scale. Do NOT "simplify"
   to pinning w=<source width> on a trimmed render — trim crops the frame
   FIRST, so w then upscales the trimmed region and inflates the quote
   (measured: 1.73" → 3.00"). The canvas scan runs on a ≤1600px render, never
   the full-res source (getImageData at 6600² ≈ 174MB = mobile crash).
   ========================================================================= */
(function () {
  'use strict';
  var MEASUREMENT_BUILD = 'ntm-20261015-rendercap-v5';
  if (window.NTMeasure && window.NTMeasure.build === MEASUREMENT_BUILD && window.NTMeasure.browserSourceRecovery === true && window.NTMeasure.serverResponseRecovery === true) return;

  var API_URL = 'https://hpz51rjda5.execute-api.us-east-1.amazonaws.com/production';
  var PRINT_DPI = 300;
  var RENDER_DPI = { vector: 72, raster: 300 };
  // pdf joined the vector path 2026-08-03: calculateInches returns the PDF's
  // PAGE BOX, not the art — a wide logo on a letter page quoted 8.5x11 while
  // every other vector type quotes the TRIMMED extent. imgix rasterises PDF at
  // exact 72dpi points (verified: unimetal-vec letter page rendered 612x792 =
  // 8.5x11.00 to the pixel; trimmed art 466x106pt = 6.47x1.47). Only psd stays
  // on the service — imgix cannot reliably rasterise it.
  var VECTOR_EXTS = ['eps', 'ai', 'svg', 'pdf'];
  var SERVICE_EXTS = ['psd'];
  // Matches bg-removal-multi.js ALPHA_TRIM_THRESHOLD — semi-transparent halo
  // pixels below this don't count as content.
  var ALPHA_THRESHOLD = 16;
  var BBOX_RENDER_MAX_W = 1600;
  // imgix RENDER CAP (By Size bands > 509 sq in, Owen 2026-10-14/15). imgix trims FIRST and
  // then FITS the output into a per-side cap. Measured on the sandbox (2026-10-15): a
  // 6600x20000 PNG loads untrimmed at 5407x16384 and trimmed (6400x19800 of art) at
  // 5296x16384 — cap 16384 for raster renders; a 7000x9000 PNG with 6600x8600 of art came
  // back 6600x8600, untouched. Vector (PDF) renders were seen capped at 8192 by the gang
  // work. So the cap is NOT hardcoded: when the source is over 8192 px on a side (fm=json,
  // one tiny CORS-open read), the untrimmed load's natural size vs the source size tells
  // whether the fit is active and what the cap is (cap = the fitted render's long side).
  // A trim render whose long side sits at that cap is CLAMPED: dividing it by the dpi gives
  // the wrong inches (a true 22x66 read 43x133). Recovery: an alpha bounding box scanned on
  // a 4096-px UNTRIMMED render (the same scan bgRemovedMeasure uses at 1600), scaled by the
  // true source size, gives the trimmed extent for transparent art (±5 source px on a
  // 20000-px side); opaque art (colour-trimmed by imgix) falls back to the source page
  // size — never a clamped number. Under the cap nothing below changes. The result carries
  // `clamped` + `srcPxW/srcPxH` so the engine can switch such a file to the raw-S3
  // production contract (pipeline.js applyRenderCap): the imgix production render
  // (`?trim=colorUnlessAlpha`, no w/h) IS this same fitted render, so production would get
  // it downscaled otherwise.
  // v5 (2026-10-15, live incident): the fm=json read and the untrimmed load are NOT free —
  // imgix takes 2.5-5.4 s to answer fm=json for a big source — and v4 awaited them on EVERY
  // raster measure (and every bg-removed measure via sourceFrame), which stalled the Ninja AI
  // arm's sizing budget into "Size unavailable". Now they run ONLY when a render comes back
  // with its long side sitting exactly at a known imgix cap (8192 / 16384), which no normal
  // upload does, and each carries a timeout. Under the cap: zero extra requests, v3 behaviour.
  var KNOWN_CAPS = [8192, 16384];
  var CAP_SCAN_PX = 4096;
  var PROBE_TIMEOUT_MS = 8000;
  function atKnownCap(w, h) { var m = Math.max(w || 0, h || 0); return KNOWN_CAPS.some(function (c) { return m >= c - 1 && m <= c; }); }
  var pixelsCache = {};
  function sourcePixels(imgixUrl, diagnostic) {
    var bare = String(imgixUrl || '').split('?')[0];
    if (pixelsCache[bare]) return pixelsCache[bare];
    var ctl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctl) ctl.abort(); }, PROBE_TIMEOUT_MS);
    pixelsCache[bare] = fetch(bare + '?fm=json', ctl ? { credentials: 'omit', signal: ctl.signal } : { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var w = j && +j.PixelWidth, h = j && +j.PixelHeight;
        return w > 0 && h > 0 ? { w: w, h: h } : null;
      })
      .catch(function () { return null; }) // a slow/failed probe never fails or delays a measure beyond its timeout
      .then(function (v) { clearTimeout(timer); return v; });
    return pixelsCache[bare];
  }
  function overProbe(px) { return !!px && (px.w > KNOWN_CAPS[0] || px.h > KNOWN_CAPS[0]); }
  // The cap in force for this source (null = none): the untrimmed load (sourceFrame, cached)
  // comes back smaller than the source only when imgix fitted it; its long side is the cap.
  // Called only after a render was seen AT a known cap.
  function renderCap(imgixUrl, px, diagnostic) {
    if (!overProbe(px)) return Promise.resolve(null);
    return sourceFrame(imgixUrl, diagnostic, true).then(function (f) { return f.cap || null; }).catch(function () { return null; });
  }
  // A trim render whose long side sits at the cap = clamped.
  function atCap(cap, renderW, renderH) {
    return cap != null && Math.max(renderW, renderH) >= cap - 1;
  }
  function capFields(px, clamped) {
    return { clamped: !!clamped, srcPxW: px ? px.w : 0, srcPxH: px ? px.h : 0 };
  }
  // Trimmed extent (source px) of a render that hit the cap. Alpha bbox on a small
  // untrimmed render, scaled by the true source size; opaque / full-frame art = page size.
  function cappedTrimExtent(imgixUrl, px, diagnostic) {
    var bare = String(imgixUrl || '').split('?')[0];
    var url = bare + (px.w >= px.h ? '?w=' : '?h=') + CAP_SCAN_PX + '&fm=png';
    return loadImage(url, true, diagnostic, PROBE_TIMEOUT_MS * 2).then(function (img) {
      var bbox = scanAlphaBBox(img);
      if (bbox && bbox.w > 0 && bbox.h > 0 && (bbox.w < bbox.frameW || bbox.h < bbox.frameH)) {
        return { w: bbox.w * (px.w / bbox.frameW), h: bbox.h * (px.h / bbox.frameH), method: 'cap-alpha-bbox' };
      }
      return { w: px.w, h: px.h, method: 'cap-page' };
    }).catch(function () { return { w: px.w, h: px.h, method: 'cap-page' }; });
  }

  // Source frame sizes, keyed by bare imgix URL. Frames never change for a
  // stored upload, so this never invalidates.
  var frameCache = {};

  // Optional caller-owned observation. Never forward URLs, messages or file data.
  function note(diagnostic, fields) {
    try { if (diagnostic) diagnostic(Object.assign({measurementBuild:MEASUREMENT_BUILD},fields)); } catch (_) {}
  }

  // Only server recovery needs a key in our uploads bucket. Browser measurement
  // must also accept existing artwork on other HTTPS origins, without mapping
  // that artwork into our bucket. Geometry transforms are never measure inputs.
  function measurementSource(value, diagnostic, browserOnly) {
    function reject(reason) {
      note(diagnostic,{failureKind:'identity',errorName:'Error',httpStatus:0,
        measurementReason:reason,measurementStep:'source_normalization'});
      return null;
    }
    if (typeof value !== 'string' || value.length > 8192) return reject('source_url_invalid');
    var source, key, parts, rawPath;
    try {
      rawPath = value.match(/^https:\/\/[^/?#]+([^?#]*)/i);
      if (!rawPath || value.indexOf('\\') !== -1) return reject('source_url_invalid');
      source = new URL(value);
      if (source.protocol !== 'https:' || source.port || source.username || source.password || source.hash)
        return reject('source_url_invalid');
      var owned = ['ninjauploads-production.imgix.net',
          'ninja-services-production-ninjauploadss3bucket-zks2mguobhe4.s3.amazonaws.com',
          'ninja-services-production-ninjauploadss3bucket-zks2mguobhe4.s3.us-east-1.amazonaws.com'].indexOf(source.hostname) !== -1;
      if (!owned && !browserOnly)
        return reject('source_host_unsupported');
      for (var pair of source.searchParams) {
        if (!/^(fm|auto|q|cs|ch)$/.test(pair[0]) || !/^[A-Za-z0-9,._-]{1,80}$/.test(pair[1]))
          return reject('source_transform_unsupported');
      }
      parts = rawPath[1].slice(1).split('/').map(function (part) { return decodeURIComponent(part); });
      if (parts.some(function (part) { return !part || part === '.' || part === '..' || /[\\/\u0000-\u001f\u007f-\u009f]/.test(part); }))
        return reject('source_key_invalid');
      key = parts.join('/');
      if (!browserOnly && unescape(encodeURIComponent(key)).length > 1024) return reject('source_key_too_long');
      if (!browserOnly && !/\.(png|jpe?g|jfif|bmp|webp|heic|heif|avif|gif|ai|pdf|svg|eps|tiff?)$/i.test(key))
        return reject('source_extension_unsupported');
      return {key:key,url:(owned ? 'https://ninjauploads-production.imgix.net' : source.origin) + '/' + parts.map(function (part) {
        return encodeURIComponent(part).replace(/[!'()*]/g,function(c){return '%' + c.charCodeAt(0).toString(16).toUpperCase();});
      }).join('/')};
    } catch (_) { return reject('source_encoding_invalid'); }
  }

  function loadImage(url, needPixels, diagnostic, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var img = new Image(), timer, settled = false;
      var started = timeoutMs ? performance.now() : 0;
      function finish(error) {
        if (settled) return;
        if (!error && timeoutMs && performance.now() - started >= timeoutMs) error = new Error('nt-measure: timeout');
        settled = true;
        if (timer) clearTimeout(timer);
        img.onload = img.onerror = null;
        if (error) { img.removeAttribute && img.removeAttribute('src'); reject(error); }
        else resolve(img);
      }
      if (needPixels) img.crossOrigin = 'anonymous';
      img.onload = function () {
        if (!img.naturalWidth || !img.naturalHeight)
          note(diagnostic, { failureKind: 'image_decode', errorName: 'Error', httpStatus: 0 });
        finish();
      };
      img.onerror = function () {
        // An Image error does not distinguish GET transport from decoding.
        note(diagnostic, { failureKind: 'image_load', errorName: 'Error', httpStatus: 0 });
        finish(new Error('nt-measure: image failed to load'));
      };
      if (timeoutMs) timer = setTimeout(function () {
        note(diagnostic, { failureKind: 'timeout', errorName: 'Error', httpStatus: 0, timeoutMs: timeoutMs });
        finish(new Error('nt-measure: timeout'));
      }, timeoutMs);
      img.src = url;
    });
  }

  // Read the dimensions of the SAME trimmed native-size render, without
  // allocating a full-resolution browser bitmap. This is quoting only: never
  // substitute these bytes or this URL for the customer's print artwork.
  function trimmedPngDimensions(imgixUrl, dpi, diagnostic) {
    var source = measurementSource(imgixUrl, diagnostic, true);
    if (!source) return Promise.resolve(null);
    var url = new URL(source.url);
    url.search = '?trim=colorUnlessAlpha&fm=png';
    var controller = new AbortController(), reader, response, timer, expired = false, phase = 'headers', parsing = false;
    var deadline = performance.now() + 12000;
    function check() { if (expired || performance.now() >= deadline) throw new Error('nt-measure: timeout'); }
    var stopped = new Promise(function (_, reject) {
      timer = setTimeout(function () { expired = true; reject(new Error('nt-measure: timeout')); controller.abort(); }, 12000);
    });
    var work = (async function () {
      response = await fetch(url.href, { signal: controller.signal, credentials: 'omit', redirect: 'error', cache: 'reload' });
      check();
      if (!response.ok || !/^image\/png(?:\s*;|$)/i.test(response.headers.get('Content-Type') || '')) {
        parsing = true; throw new Error('nt-measure: invalid PNG response');
      }
      phase = 'body';
      reader = response.body.getReader();
      var header = new Uint8Array(33), used = 0;
      while (used < header.length) {
        var row = await reader.read(); check();
        if (row.done) { parsing = true; throw new Error('nt-measure: incomplete PNG header'); }
        var count = Math.min(header.length - used, row.value.byteLength);
        header.set(row.value.subarray(0, count), used); used += count;
      }
      parsing = true;
      var view = new DataView(header.buffer);
      if ([137,80,78,71,13,10,26,10].some(function (value, index) { return header[index] !== value; }) ||
          view.getUint32(8) !== 13 || view.getUint32(12) !== 0x49484452) throw new Error('nt-measure: invalid PNG header');
      // Verify IHDR integrity before using its geometry for a price.
      var crc = 0xffffffff;
      for (var i = 12; i < 29; i++) {
        crc ^= header[i];
        for (var bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0);
      }
      var width = view.getUint32(16), height = view.getUint32(20);
      if (((crc ^ 0xffffffff) >>> 0) !== view.getUint32(29) || !width || !height || width > 65536 || height > 65536)
        throw new Error('nt-measure: invalid PNG geometry');
      check();
      if (!atKnownCap(width, height)) return fromInches(width / dpi, height / dpi, 'trim-png-header', capFields(null, false));
      var bare = url.origin + url.pathname;
      var px = await sourcePixels(bare, diagnostic); // a header AT a known cap: confirm the fit (timed)
      var cap = px ? await renderCap(bare, px, diagnostic) : null;
      if (!atCap(cap, width, height)) return fromInches(width / dpi, height / dpi, 'trim-png-header', capFields(px, false));
      var t = await cappedTrimExtent(bare, px, diagnostic);
      return fromInches(t.w / dpi, t.h / dpi, 'trim-png-header-' + t.method, capFields(px, true));
    })();
    return Promise.race([work, stopped]).catch(function (error) {
      note(diagnostic, { failureKind: expired || performance.now() >= deadline ? 'timeout' : response && !response.ok ? 'http' :
        !response ? 'network_or_cors' : parsing ? 'invalid_format' : 'response_body', errorName: error && error.name || 'Error', httpStatus: response && response.status || 0,
        transportPhase: phase });
      return null;
    }).finally(function () {
      clearTimeout(timer); controller.abort();
      try { var pending = reader ? reader.cancel() : response && response.body && response.body.cancel();
        if (pending && pending.catch) pending.catch(function () {});
        if (reader) reader.releaseLock();
      } catch (_) {}
    });
  }

  var trimServiceCache = new Map(), trimServicePending = new Map();
  function serverTrimDimensions(imgixUrl, dpi, diagnostic) {
    // Ineligible recovery is not another failed measurement. Keep the actual
    // browser load/header failure rather than replacing it with a host rejection.
    var source = measurementSource(imgixUrl);
    if (!source) return Promise.resolve(null);
    var key = source.key, cached = trimServiceCache.get(key);
    if (cached && cached.expires > Date.now()) return Promise.resolve(cached.result);
    if (trimServicePending.has(key)) return trimServicePending.get(key);
    var controller = new AbortController(), response, timer, phase = 'headers', requestId, failureKind;
    var expires = performance.now() + 21000;
    function check() { if (performance.now() >= expires) throw new Error('measurement timeout'); }
    var timeout = new Promise(function (_, reject) {
      timer = setTimeout(function () { reject(new Error('measurement timeout')); controller.abort(); }, 21000);
    });
    // A small JSON response may stall after HTTP 200. Fence its body separately,
    // then retry this read-only request once within the original 21s allowance.
    // Late responses cannot cache dimensions or win after a retry/cancellation.
    async function attempt() {
      var requestController = new AbortController(), bodyTimer, requestTimer, stopped = false;
      response = null; phase = 'headers'; requestId = undefined; failureKind = undefined;
      var abort = function () { requestController.abort(); };
      controller.signal.addEventListener('abort', abort, {once:true});
      var requestExpires = Math.min(expires, performance.now() + 18000);
      function checkAttempt() {
        check();
        if (stopped || controller.signal.aborted || performance.now() >= requestExpires)
          throw Object.assign(new Error('measurement timeout'), {failureKind:'timeout'});
      }
      var rejectDeadline;
      var deadline = new Promise(function (_, reject) {
        rejectDeadline = reject;
        requestTimer = setTimeout(function () {
          stopped = true; reject(Object.assign(new Error('measurement timeout'), {failureKind:'timeout'})); requestController.abort();
        }, Math.max(1, requestExpires-performance.now()));
      });
      try {
        return await Promise.race([deadline, (async function () {
          checkAttempt();
          var current = await fetch(API_URL + '/calculateInches?mode=trimmed&s3Key=' + encodeURIComponent(key),
            { signal: requestController.signal, credentials:'omit', redirect:'error', cache:'no-store' });
          checkAttempt(); response = current; phase = 'body';
          bodyTimer = setTimeout(function () {
            stopped = true; rejectDeadline(Object.assign(new Error('measurement timeout'), {failureKind:'timeout'})); requestController.abort();
          }, Math.max(1, Math.min(3000, requestExpires-performance.now())));
          var body = await current.json(); checkAttempt();
          requestId = body && /^[a-zA-Z0-9_-]{1,80}$/.test(body.requestId || '') ? body.requestId : undefined;
          if (!current.ok || !body || body.success !== true) throw new Error('measurement failed');
          var data = body.data;
          if (!data || data.contract !== 'trim-v1' || data.dpi !== dpi ||
              ![data.renderWidth, data.renderHeight].every(function (v) { return Number.isInteger(v) && v > 0 && v <= 65536; }))
            throw new Error('invalid measurement');
          return data;
        })()]);
      } catch (error) {
        failureKind = error.failureKind || (response && !response.ok ? 'http' : !response ? 'network_or_cors' : 'validation');
        throw error;
      } finally {
        stopped = true; clearTimeout(bodyTimer); clearTimeout(requestTimer);
        controller.signal.removeEventListener('abort', abort); requestController.abort();
      }
    }
    var work = Promise.race([timeout, (async function () {
      var data;
      for (var index=0; index<2; index++) {
        try { data = await attempt(); break; }
        catch (error) {
          var retryable = failureKind === 'timeout' || failureKind === 'network_or_cors' ||
            failureKind === 'http' && response && response.status >= 500;
          // Invalid content, geometry and 4xx render rejections need a different
          // repair, not another identical request. No image upload is retried.
          if (index || !retryable || expires-performance.now()<1000) throw error;
          note(diagnostic,{failureKind:failureKind,httpStatus:response && response.status || 0,transportPhase:phase,
            requestId:requestId,measurementStep:'server_recovery',measurementReason:'server_recovery_failed'});
        }
      }
      check();
      var result = fromInches(data.renderWidth / dpi, data.renderHeight / dpi, 'trim-server-header');
      if (trimServiceCache.size >= 128) trimServiceCache.delete(trimServiceCache.keys().next().value);
      trimServiceCache.set(key, { result: result, expires: Date.now() + 300000 });
      return result;
    })()]).catch(function (error) {
      note(diagnostic, { failureKind: performance.now() >= expires ? 'timeout' : failureKind || (response && !response.ok ? 'http' :
        !response ? 'network_or_cors' : 'validation'), httpStatus: response && response.status || 0,
        errorName: error.name || 'Error', transportPhase: phase, requestId: requestId,
        measurementStep:'server_recovery',measurementReason:'server_recovery_failed' });
      return null;
    }).finally(function () { clearTimeout(timer); controller.abort(); trimServicePending.delete(key); });
    trimServicePending.set(key, work);
    return work;
  }

  // Alpha bounding box of a decoded render, in that render's pixels.
  // Returns null when the canvas is tainted (CORS) or nothing is opaque —
  // callers fall back to the trim-only measure.
  function scanAlphaBBox(img) {
    var w = img.naturalWidth;
    var h = img.naturalHeight;
    if (!w || !h) return null;
    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    var data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (e) {
      return null;
    }
    var minX = w, maxX = -1, minY = h, maxY = -1;
    for (var y = 0; y < h; y++) {
      var rowStart = y * w * 4;
      for (var x = 0; x < w; x++) {
        if (data[rowStart + x * 4 + 3] > ALPHA_THRESHOLD) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;
    return { w: maxX - minX + 1, h: maxY - minY + 1, frameW: w, frameH: h };
  }

  function serviceInches(s3Key, diagnostic) {
    var response, phase = 'headers';
    return fetch(API_URL + '/calculateInches?s3Key=' + encodeURI(s3Key))
      .then(function (r) {
        response = r; phase = 'body';
        if (r.ok === false) note(diagnostic, { failureKind: 'http', errorName: 'Error', httpStatus: r.status, transportPhase: 'headers' });
        return r.json();
      })
      .then(function (res) {
        if (res && res.success === true && res.data && res.data.dimensions &&
            res.data.dimensions.width && res.data.dimensions.height) {
          return { inW: res.data.dimensions.width, inH: res.data.dimensions.height };
        }
        if (!response || response.ok !== false)
          note(diagnostic, { failureKind: 'validation', errorName: 'Error', httpStatus: response && response.status || 0, transportPhase: phase });
        return null;
      })
      .catch(function (error) {
        if (!response || response.ok !== false) note(diagnostic, {
          failureKind: !response ? 'network_or_cors' : error && error.name === 'SyntaxError' ? 'invalid_json' : 'response_body',
          errorName: error && error.name || 'Error', httpStatus: response && response.status || 0, transportPhase: phase
        });
        return null;
      });
  }

  function sourceFrame(imgixUrl, diagnostic, probe) {
    if (frameCache[imgixUrl]) return Promise.resolve(frameCache[imgixUrl]);
    // The no-param (untrimmed) load is fitted into the cap too. Only when that load comes back AT
    // a known cap (or the caller already saw a capped render: `probe`) is the true source size read
    // (fm=json, timed) to report the real frame and the cap; otherwise the load IS the frame — v3.
    return loadImage(imgixUrl, false, diagnostic, PROBE_TIMEOUT_MS).then(function (img) {
      var nw = img.naturalWidth, nh = img.naturalHeight;
      if (!probe && !atKnownCap(nw, nh)) { var plain = { w: nw, h: nh, px: null, clamped: false, cap: null }; frameCache[imgixUrl] = plain; return plain; }
      return sourcePixels(imgixUrl, diagnostic).then(function (px) {
        var fitted = overProbe(px) && Math.max(nw, nh) < Math.max(px.w, px.h) - 1;
        var f = fitted
          ? { w: px.w, h: px.h, px: px, clamped: true, cap: Math.max(nw, nh) }
          : { w: nw, h: nh, px: px, clamped: false, cap: null };
        frameCache[imgixUrl] = f;
        return f;
      });
    });
  }

  function fromInches(inW, inH, method, extra) {
    var r = {
      pxW: Math.round(inW * PRINT_DPI),
      pxH: Math.round(inH * PRINT_DPI),
      inW: inW,
      inH: inH,
      method: method
    };
    if (extra) for (var k in extra) if (Object.prototype.hasOwnProperty.call(extra, k)) r[k] = extra[k];
    return r;
  }

  // Trim-only render at native size — the default measure for anything the
  // client can decode. No w/h, no bg-remove, ever (see header). fm=png is
  // pinned for vectors only: without it imgix serves a JPEG render of an
  // .ai/.eps (same dimensions, but pin the proven v4 measure params); for
  // rasters the output format never changes the dimensions, and skipping
  // fm=png keeps a big JPG's measure render at JPG weight.
  function trimMeasure(imgixUrl, dpi, method, diagnostic, recover, serverRecovery) {
    if (recover) {
      var source = measurementSource(imgixUrl, diagnostic, true);
      if (!source) return Promise.resolve(null);
      imgixUrl = source.url;
    }
    var params = dpi === RENDER_DPI.vector
      ? '?trim=colorUnlessAlpha&fm=png'
      : '?trim=colorUnlessAlpha';
    var run = loadImage(imgixUrl + params, false, diagnostic, recover ? 6000 : 0).then(function (img) {
      var nw = img.naturalWidth, nh = img.naturalHeight;
      if (!nw || !nh) return null;
      // v3 path for everything that is not sitting exactly at a known imgix cap: no extra requests
      if (!atKnownCap(nw, nh)) return fromInches(nw / dpi, nh / dpi, method, capFields(null, false));
      // a render AT a cap: read the true source size (timed) and confirm the fit before recovering
      return sourcePixels(imgixUrl, diagnostic).then(function (px) {
        if (!px) return fromInches(nw / dpi, nh / dpi, method, capFields(null, false));
        return renderCap(imgixUrl, px, diagnostic).then(function (cap) {
          if (!atCap(cap, nw, nh)) return fromInches(nw / dpi, nh / dpi, method, capFields(px, false));
          return cappedTrimExtent(imgixUrl, px, diagnostic).then(function (t) {
            return fromInches(t.w / dpi, t.h / dpi, method + '-' + t.method, capFields(px, true));
          });
        });
      });
    });
    if (!recover) return run;
    return run.catch(function () { return null; }).then(function (result) {
      return result || trimmedPngDimensions(imgixUrl, dpi, diagnostic);
    }).then(function (result) {
      return result || (serverRecovery ? serverTrimDimensions(imgixUrl, dpi, diagnostic) : null);
    });
  }

  // Content-fraction measure of a bg-removed raster (see header). Any failure
  // (CORS taint, empty bbox, load error) returns null and the caller falls
  // back to the pre-removal trimmed size — conservative, never garbage.
  // modelParam (optional): the imgix bg-remove-model of the render the
  // CUSTOMER CHOSE (v4 3-option picker; null/undefined = imgix default).
  // Without it the quote measured the DEFAULT model regardless of choice —
  // on sources where the default model leaves full-frame residue the
  // content fraction hit ~1.0 and the customer was quoted the whole source
  // frame (Owen 2026-08-10: Eiffel 12x12 quoted 12.05x11.06 with a ~3.2in
  // tower selected on model 4). The 7-11 fix measured the chosen render;
  // the 7-29 move into this module dropped that — this restores it.
  function bgRemovedMeasure(imgixUrl, modelParam, diagnostic) {
    return sourceFrame(imgixUrl, diagnostic).then(function (frame) {
      if (!frame.w || !frame.h) return null;
      var renderW = Math.min(BBOX_RENDER_MAX_W, frame.w);
      var url = imgixUrl + '?bg-remove=true' +
        (modelParam ? '&bg-remove-model=' + modelParam : '') +
        '&fm=png&w=' + renderW;
      return loadImage(url, true, diagnostic).then(function (img) {
        var bbox = scanAlphaBBox(img);
        if (!bbox) return null;
        var contentSrcW = bbox.w * (frame.w / bbox.frameW);
        var contentSrcH = bbox.h * (frame.h / bbox.frameH);
        return fromInches(
          contentSrcW / RENDER_DPI.raster,
          contentSrcH / RENDER_DPI.raster,
          'raster-bgr-bbox',
          capFields(frame.px, frame.clamped)
        );
      });
    });
  }

  /**
   * Measure an upload for QUOTING.
   *
   * @param {Object} opts
   * @param {string} opts.imgixUrl  bare imgix URL of the upload (no params)
   * @param {string} opts.ext      file extension, any case
   * @param {string} [opts.s3Key]   stored object key / upload file name —
   *                                required for pdf/psd (service mode)
   * @param {boolean} [opts.bgRemove] quote the post-background-removal extent
   *                                (rasters only; vectors ship untouched and
   *                                never re-quote for BGR)
   * @param {number}  [opts.bgRemoveModel] imgix bg-remove-model of the
   *                                CHOSEN render (omit for the default model)
   * @param {Function} [opts.onDiagnostic] optional sanitized failure observation;
   *                                does not change measurement or issue requests
   * @returns {Promise<{pxW,pxH,inW,inH,method,clamped,srcPxW,srcPxH}|null>} px on the 300dpi print
   *          basis; `clamped` = the trimmed render sat at imgix's per-side render cap (inches
   *          were recovered, see KNOWN_CAPS) with the true source px beside it;
   *          basis, inches as numbers. null = measurement failed; callers
   *          keep their own fallback (do NOT invent one here).
   */
  function measure(opts) {
    var ext = String(opts.ext || '').toLowerCase();
    var run, observed = false;
    var diagnostic = typeof opts.onDiagnostic === 'function' ? function (fields) {
      observed = true; note(opts.onDiagnostic, fields);
    } : null;

    if (opts.pageBox && opts.s3Key) {
      // GANG sheets are quoted at their FULL sheet size, and a large sheet
      // (>~114in on an axis) exceeds imgix's ~8192px render cap — a client
      // render CLAMPS and mis-measures (a true 22x360in sheet rendered as
      // 7x113.8, failing the ~22in width gate). The detect service reads the
      // source's true page dimensions WITHOUT rendering, so gang routes there —
      // this is exactly what the legacy gang flow (pdp_transferBySize) did, and
      // why real large gang orders came through it. Falls back to the client
      // render only if the service misses. Single-transfer NEVER sets pageBox,
      // so its trim-measure below is unchanged (the service over-quotes a single
      // design to its whole page — the reason pdf moved off the service 2026-08).
      run = serviceInches(opts.s3Key, diagnostic).then(function (r) {
        if (r) return fromInches(r.inW, r.inH, 'service-gang');
        if (SERVICE_EXTS.indexOf(ext) !== -1) return null;
        return trimMeasure(
          opts.imgixUrl,
          VECTOR_EXTS.indexOf(ext) !== -1 ? RENDER_DPI.vector : RENDER_DPI.raster,
          'gang-fallback', diagnostic
        );
      });
    } else if (SERVICE_EXTS.indexOf(ext) !== -1) {
      // psd ONLY — and the sole exception to the post-trim rule (Owen
      // 2026-08-03: "size needs to be calculated post trim" for everything),
      // because imgix cannot rasterise psd for a client-side trim measure.
      // The service reports the psd's canvas, not the trimmed art.
      run = serviceInches(opts.s3Key, diagnostic).then(function (r) {
        return r ? fromInches(r.inW, r.inH, 'service-s3key') : null;
      });
    } else if (VECTOR_EXTS.indexOf(ext) !== -1) {
      run = trimMeasure(opts.imgixUrl, RENDER_DPI.vector, 'vector-72', diagnostic, opts.recover === true, opts.serverRecovery === true);
    } else if (opts.bgRemove) {
      // .catch BEFORE the fallback: a rejected bg render (load failure) must
      // still fall back to the pre-removal trimmed size, same as a null bbox.
      run = bgRemovedMeasure(opts.imgixUrl, opts.bgRemoveModel, diagnostic)
        .catch(function () { return null; })
        .then(function (r) {
          return r || trimMeasure(opts.imgixUrl, RENDER_DPI.raster, 'raster-trim-fallback', diagnostic);
        });
    } else {
      run = trimMeasure(opts.imgixUrl, RENDER_DPI.raster, 'raster-trim', diagnostic, opts.recover === true, opts.serverRecovery === true);
    }

    return run.then(function (result) {
      if (!result && !observed) note(diagnostic, { failureKind: 'validation', errorName: 'Error', httpStatus: 0 });
      return result;
    }).catch(function (err) {
      if (!observed) note(diagnostic, { failureKind: 'unknown', errorName: err && err.name || 'Error', httpStatus: 0 });
      console.log('ERROR NTMeasure.measure', err && err.message);
      return null;
    });
  }

  window.NTMeasure = Object.freeze({ measure: measure, build: MEASUREMENT_BUILD, recoveryVersion:2, browserSourceRecovery:true, serverResponseRecovery:true });
})();
